import { useState, memo } from "react";
import AdminBreadcrumb from "@/features/admin/components/AdminBreadcrumb";
import TeamFilter from "../components/TeamManagement/TeamFilter";
import TeamNavigation from "../components/TeamManagement/TeamNavigation";
import Navbar from "@/components/Common/Navbar";
import {
  HashKeysActionButtons,
  HashKeysContent,
  HashKeysErrorState,
  HashKeysLoadingState,
} from "../components/HashKeys";
import {
  useHashKeysData,
  useHashKeysSelection,
  useHashKeysActions,
} from "../hooks";
import type { SortOption } from "@/features/auth/types/hashKeys.types";

function TeamManagementContainer() {
  const [sortBy, setSortBy] = useState<SortOption>("alphabetical_asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState<string>("all"); // Add this line

  const { hashKeys, rawHashKeys, statusCounts, isLoading, error } =
    useHashKeysData(searchQuery, sortBy, filterRole); // Add filterRole parameter

  const {
    selectionState,
    handleKeySelect,
    handleSelectAll,
    handleClearSelection,
  } = useHashKeysSelection(hashKeys);

  const actions = useHashKeysActions(
    handleKeySelect,
    handleSelectAll,
    Array.from(selectionState.selectedKeys),
    handleClearSelection,
  );

  const handleClearSearch = () => setSearchQuery("");

  if (isLoading) {
    return (
      <HashKeysLoadingState
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
    );
  }

  if (error) {
    return (
      <HashKeysErrorState
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        sortBy={sortBy}
        setSortBy={setSortBy}
        onRetry={() => window.location.reload()}
        filterRole={filterRole}
        onFilterChange={setFilterRole}
      />
    );
  }

  return (
    <main className="scrollbar-thin flex h-screen min-h-0 flex-1 flex-col gap-7 overflow-y-auto p-3 px-4 pr-0 pl-6 transition-all duration-300">
      <div className="mb-5 flex-shrink-0">
        <Navbar subtitle="manager" />
      </div>

      <div className="flex-shrink-0 px-2 transition-all duration-300 sm:px-4 md:px-8 lg:px-16 xl:px-[120px]">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex md:flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between">
            <AdminBreadcrumb title="Team Management" />
            <TeamNavigation
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 px-2 transition-all duration-300 sm:px-4 md:px-8 lg:px-16 xl:px-[120px]">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex min-h-0 w-full flex-row justify-start gap-8 pb-20">
            <div className="flex w-full justify-between gap-8 md:flex-col-reverse lg:flex-col-reverse xl:flex-row">
              {/* ACTION BUTTONS */}
              <HashKeysActionButtons
                selectedCount={selectionState.selectedKeys.size}
                onAddNew={actions.onAddNew!}
                onDelete={actions.onBulkDelete!}
                isDeleting={actions.isBulkDeleting}
              />

              {/* MAIN CONTENT AREA - Add overflow wrapper */}
              <div className="w-full overflow-x-auto">
                <div className="min-w-[800px]">
                  <HashKeysContent
                    hashKeys={hashKeys}
                    selectionState={selectionState}
                    actions={actions}
                    searchQuery={searchQuery}
                    onClearSearch={handleClearSearch}
                    isDeleting={actions.isBulkDeleting}
                  />
                </div>
              </div>

              {/* FILTER SIDEBAR */}
              <TeamFilter
                sortBy={sortBy}
                onSortChange={(value: string) => setSortBy(value as SortOption)}
                filterRole={filterRole}
                onFilterChange={setFilterRole}
                roleCounts={statusCounts}
                totalMembers={rawHashKeys.length}
                enableFilter={true}
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default memo(TeamManagementContainer);
